import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';

const MasterplanPage = () => {
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    setSeasons(mockData.masterplanSeasons);
  }, []);

  const seasonImages = {
    "Blossom": "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/7j9kx0mg_Image%2043.jpeg",
    "Lush": "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/sseg3nkq_Image%2044.jpeg",
    "Amber": "https://images.unsplash.com/photo-1614841165438-05ea96a60a21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxzZWFzb25hbCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQyODV8MA&ixlib=rb-4.1.0&q=85",
    "Pure": "https://images.unsplash.com/photo-1614841221867-4094841053b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzZWFzb25hbCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQyODV8MA&ixlib=rb-4.1.0&q=85"
  };

  const seasonGradients = {
    "Blossom": "from-pink-500/20 to-rose-500/20",
    "Lush": "from-green-500/20 to-emerald-500/20",
    "Amber": "from-amber-500/20 to-orange-500/20",
    "Pure": "from-gray-500/20 to-slate-500/20"
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with 3D Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* 3D Layered Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/tcwvql9e_Image%2067.jpeg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(2px) brightness(0.75)'
            }}
          ></div>
          
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-emerald-900/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <Badge variant="outline" className="text-emerald-300 border-emerald-300/50 bg-emerald-900/20 px-4 py-2 text-lg backdrop-blur-sm">
              Great Hallingbury, Hertfordshire
            </Badge>
            <Badge variant="outline" className="text-amber-300 border-amber-300/50 bg-amber-900/20 px-4 py-2 text-lg backdrop-blur-sm">
              In Development
            </Badge>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white drop-shadow-2xl mb-8">
            The National Memorial Garden
          </h1>
          <p className="text-xl md:text-2xl text-white mb-10 max-w-5xl mx-auto leading-relaxed drop-shadow-xl">
            Our flagship £3 million development featuring innovative four-season design themes. 
            Located strategically near the M11, M25, and M1 motorways for exceptional UK-wide accessibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/contact">
              <Button variant="outline" size="lg" className="border-2 border-white/80 text-white hover:bg-white/20 px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300 backdrop-blur-sm">
                Discuss This Project
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Overview with Rich Visuals */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-amber-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">A Revolutionary Approach</h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                The National Memorial Garden represents a groundbreaking approach to memorial space design. 
                Rather than traditional cemetery layouts, we've created four distinct themed areas, each 
                representing a season and offering unique experiences throughout the year.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center p-6 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl shadow-lg">
                  <div className="text-3xl font-bold text-amber-600 mb-2">£3M</div>
                  <div className="text-gray-700 font-medium">Investment</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl shadow-lg">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">UK-wide</div>
                  <div className="text-gray-700 font-medium">Accessibility</div>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  "Contemporary chapel and service buildings",
                  "Diverse botanical areas and tranquil water features",
                  "Advanced accessibility features throughout",
                  "On-site café, florist, and visitor amenities"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-md">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mr-4"></div>
                    <span className="text-gray-800 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div 
                className="aspect-square bg-cover bg-center rounded-3xl shadow-2xl relative overflow-hidden"
                style={{
                  backgroundImage: "url('https://customer-assets.emergentagent.com/job_fenix-company/artifacts/5ucykfrt_Picture%205.png')"
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Four Seasons with Rich Imagery */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1598161966801-4c8898ba6305?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxwZWFjZWZ1bCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQzMDN8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 to-emerald-900/75"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Four Seasons. Four Quarters. Four Themes.
            </h2>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              Each quarter of our memorial garden represents a different season, creating year-round 
              beauty and meaning for families and visitors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {seasons.map((season, index) => {
              const gradientClass = seasonGradients[season.name] || "from-emerald-500/20 to-teal-500/20";
              const seasonImage = seasonImages[season.name];
              
              return (
                <Card key={index} className="overflow-hidden border-0 bg-white/95 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105">
                  <div 
                    className="h-64 bg-cover bg-center relative"
                    style={{
                      backgroundImage: `url('${seasonImage}')`
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-t ${gradientClass} to-black/60`}></div>
                    <div className="absolute top-6 left-6">
                      <Badge className="bg-white/90 text-gray-800 border-0 px-4 py-2 font-bold">
                        {season.season}
                      </Badge>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="text-3xl font-bold text-white drop-shadow-lg">{season.name}</h3>
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <p className="text-gray-700 mb-8 leading-relaxed text-lg">{season.description}</p>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-4 text-lg">Key Features</h4>
                        <div className="grid grid-cols-1 gap-3">
                          {season.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl">
                              <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mr-4"></div>
                              <span className="text-gray-700 font-medium">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                        <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                          <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Color Theme</h5>
                          <p className="text-sm text-gray-800 font-medium">{season.colorTheme}</p>
                        </div>
                        <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
                          <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Planting</h5>
                          <p className="text-sm text-gray-800 font-medium">{season.plantingType}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Benefits with Map */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">Strategic Location</h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Positioned in Great Hallingbury, Hertfordshire, with exceptional accessibility 
              across the UK via major motorway networks.
            </p>
          </div>

          {/* Map Image */}
          <div className="mb-16 text-center">
            <div className="relative inline-block rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_fenix-company/artifacts/l7thvcho_Image%2025-08-2025%20at%2016.03.jpeg"
                alt="Strategic Location Map showing M11, M25, M1 access"
                className="w-full max-w-4xl h-auto"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                title: "M11 Access", 
                description: "Direct access to the M11 motorway, connecting easily to London, Cambridge, and the South East."
              },
              { 
                title: "M25 Connectivity", 
                description: "Close proximity to the M25 orbital motorway, providing access to all areas of Greater London and beyond."
              },
              { 
                title: "M1 Network", 
                description: "Connection to the M1, enabling service to the Midlands, North England, and Scotland."
              }
            ].map((location, index) => (
              <Card key={index} className="border-0 bg-white/90 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{location.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{location.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Rich Background */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1544862870-a4d5e74dc313?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQzMDN8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-amber-900/90"></div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="backdrop-blur-sm bg-white/10 rounded-3xl p-12 border border-white/20 shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Experience The Masterplan</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              The National Memorial Garden represents the future of memorial space design. 
              Discover how we're creating something truly special in Great Hallingbury.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300">
                  Discuss This Project
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-4 text-lg font-semibold backdrop-blur-sm shadow-2xl transform hover:scale-105 transition-all duration-300">
                  Our Development Process
                  <ArrowRight className="ml-3" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MasterplanPage;